import { SelectMenuProps, TimerangePickerProps } from './TimerangePicker.types';
import { formatRangeCap, isRangeExceedingCap } from './TimerangePicker.utils';
import { Alert, ClickAwayListener, List, ListItemButton, ListItemText, Popper, Stack, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import TimeButton from 'components/buttons/TimeButton';
import { InfoTooltip } from 'components/feedback/InfoTooltip';
import Panel from 'components/panels/Panel/Panel';
import { isAfter, isBefore, setHours, setMilliseconds, setMinutes, setSeconds, subMilliseconds } from 'date-fns';
import { useBreakpoint } from 'hooks/useBreakpoint';
import useScreenSize from 'hooks/useScreenSize';
import _ from 'lodash';
import { useSnackbar } from 'notistack';
import { MouseEvent, TouchEvent, useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import { Range } from 'types';
import { SHORT_DATE_TIME_FORMAT } from 'utils/constants';
import { Icons } from 'greener-ui-library';

function SelectMenu({ options, onClick, selectedOption, availableRange }: SelectMenuProps) {
  const handleSelectedIndex = (timePeriod: { value: number; label: string; rangeKey?: string | null }) =>
    onClick(timePeriod);

  function isValidOption(option: { value: number; label: string }) {
    const optionFrom = subMilliseconds(new Date(), option.value);
    const optionTo = new Date();

    if (!availableRange) {
      return true;
    }

    if (!availableRange?.from) {
      return false;
    }

    const availableFrom = new Date(availableRange.from);
    const availableTo = availableRange.to ? new Date(availableRange.to) : new Date();

    const optionStartsInside = !isBefore(optionFrom, availableFrom);
    const optionEndsInside = !isAfter(optionTo, availableTo);

    return optionStartsInside && optionEndsInside;
  }

  return (
    <List>
      {options.map((option) => (
        <ListItemButton
          key={option.label}
          selected={selectedOption?.value === option?.value}
          onClick={() => handleSelectedIndex(option)}
          disabled={!isValidOption(option)}
        >
          <ListItemText primary={option.label} primaryTypographyProps={{ variant: 'h1' }} />
        </ListItemButton>
      ))}
    </List>
  );
}

export function TimerangePicker({
  range,
  applyRange,
  resetRange,
  availableRange,
  showApplyAvailableRange = false,
  showTimes = true,
  rangeCap,
  buttonTextRangeFormatter,
  disabled,
  buttonSize = 'large',
}: TimerangePickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { isMobile } = useBreakpoint();

  const formattedRangeCap = formatRangeCap(rangeCap);
  const rangeCapMsg = formattedRangeCap ? t(formattedRangeCap.msg, formattedRangeCap.options) : null;

  const [exceedsCap, setExceedsCap] = useState(false);

  // Store a copy of the provided range.
  // We need this to see if the provided range was changed through different means than via this component.
  // We prefer this approach over a useEffect as it requires less rerenders.
  const [providedRange, setProvidedRange] = useState<Range>({
    from: range?.from,
    to: range?.to,
  });

  /*
  State used to change the selected date in the input, but preventing the range to change immediately,
  thus preventing a rerender. Range should only change when clicking on "apply range",
  or by clicking an option in the "relative ranges" menu
  */
  const [localRange, setLocalRange] = useState<Range>({
    from: range?.from,
    to: range?.to,
  });

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const { breakpoints } = useScreenSize();

  useEffect(() => {
    if (!!range?.from && !_.isEqual(range, providedRange)) {
      setProvidedRange(range);
      setLocalRange(range);
    }
  }, [range?.from, providedRange, setProvidedRange, setLocalRange, range]);

  const rangeFromNowOptions = [
    { value: 3600000, label: t('widgets.timerangePicker.lastHour') },
    { value: 10800000, label: t('widgets.timerangePicker.last3Hours') },
    { value: 21600000, label: t('widgets.timerangePicker.last6Hours') },
    { value: 32400000, label: t('widgets.timerangePicker.last9Hours') },
    { value: 43200000, label: t('widgets.timerangePicker.last12Hours') },
    { value: 86400000, label: t('widgets.timerangePicker.lastDay') },
    { value: 172800000, label: t('widgets.timerangePicker.last2Days') },
    { value: 604800000, label: t('widgets.timerangePicker.last7Days') },
    { value: 2592000000, label: t('widgets.timerangePicker.last30Days') },
  ];

  /*
  Determine if an option should be selected based on the difference between defaultValue.to and defaultValue.from
  Compares the values of the rangeFromNowOptions array and also checks if the current value relative from "now",
  if not it means that it is the past.
  */
  const selectedOption = rangeFromNowOptions.find((option) => {
    const now = new Date();
    const maxAllowedOffset = 60 * 1000; //1 min
    const optionDuration = (range?.to as number) - (range?.from as number);
    const offsetFromNow = range?.to ? now.getTime() - (range?.to as Date).getTime() : 0;
    return option.value === optionDuration && offsetFromNow <= maxAllowedOffset;
  });

  const handleToggle = () => setOpen((prevOpen) => !prevOpen);

  const handleClose = (event: globalThis.MouseEvent | globalThis.TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
      return;
    }
    setOpen(false);
  };

  const handleReset = (event: MouseEvent | TouchEvent) => {
    if (resetRange) {
      resetRange();
    }

    //Double casting since handle close expects the global mouse events,
    // but Button expects the react mouse events.

    handleClose(event as unknown as globalThis.MouseEvent | globalThis.TouchEvent);
  };

  function onFromChange(from: Date | number) {
    if (!!localRange?.to && from >= localRange?.to) {
      return;
    }

    if (!!rangeCap && isRangeExceedingCap({ from, to: localRange.to }, rangeCap)) {
      setExceedsCap(true);
    } else {
      setExceedsCap(false);
    }

    const startOfDay = setHours(setMinutes(setSeconds(setMilliseconds(from, 0), 0), 0), 0);
    setLocalRange({
      ...localRange,
      from: showTimes ? from : startOfDay,
      isRelative: false,
    });
  }

  function onToChange(to: number | Date) {
    if (!!localRange?.from && localRange.from >= to) {
      return;
    }

    if (!!rangeCap && isRangeExceedingCap({ from: localRange.from, to }, rangeCap)) {
      setExceedsCap(true);
    } else {
      setExceedsCap(false);
    }

    const endOfDay = setMilliseconds(setSeconds(setMinutes(setHours(to, 23), 59), 59), 999);
    setLocalRange({
      ...localRange,
      to: showTimes ? to : endOfDay,
      isRelative: false,
    });
  }

  function onClickApplyChanges() {
    applyRange(localRange);
    setOpen(false);
  }

  const onChangeRelative = (timePeriod: { value: number; label: string }) => {
    const now = new Date();
    const newValue = {
      from: new Date(now.getTime() - timePeriod.value),
      to: now,
      isRelative: true,
    };
    setLocalRange(newValue);
    applyRange(newValue);
    setOpen(false);
  };

  const applyAvailableRange = () => {
    setLocalRange(availableRange);
    applyRange(availableRange);
    setOpen(false);
  };

  //Reset the range if the provided range exceeds the rangeCap
  useEffect(() => {
    if (!!rangeCap && !!rangeCapMsg && isRangeExceedingCap(range, rangeCap)) {
      enqueueSnackbar(rangeCapMsg, { variant: 'info' });
      resetRange();
    }
  }, [resetRange, range, rangeCap, rangeCapMsg, enqueueSnackbar, formatRangeCap, isRangeExceedingCap]);

  return (
    <>
      <div ref={anchorRef} style={{ width: '100%' }}>
        <Stack
          display="flex"
          alignItems={isMobile ? 'flex-end' : 'center'}
          justifyContent="flex-end"
          direction={isMobile ? 'column' : 'row'}
          gap={1}
        >
          {localRange.isRelative && !isMobile && (
            <InfoTooltip tooltipText={t('widgets.timerangePicker.refresh')} iconStyle={{ width: 18, height: 18 }} />
          )}
          <TimeButton
            range={localRange}
            onClick={handleToggle}
            selectedOption={selectedOption}
            endIcon={
              <Icons.ArrowDropDown
                sx={{
                  color: (theme) => theme.palette.grey[700],
                  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            }
            rangeFormatter={buttonTextRangeFormatter}
            disabled={disabled}
            size={buttonSize}
          />
          {isMobile && localRange.isRelative && !open && (
            <Typography variant="caption">{t('widgets.timerangePicker.refresh')}</Typography>
          )}
        </Stack>
      </div>
      <ClickAwayListener onClickAway={handleClose} mouseEvent="onMouseUp">
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-end"
          sx={{ minHeight: 300, maxWidth: breakpoints.sm, zIndex: (theme) => theme.zIndex.modal }}
        >
          <Panel>
            <div
              style={{
                display: 'flex',
              }}
            >
              <div
                id="datePickerContainer"
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  marginRight: 20,
                }}
              >
                <DatePicker
                  key="from"
                  selected={localRange.from ? new Date(localRange.from) : null}
                  onChange={(date: Date | null) => onFromChange(date as Date)}
                  selectsStart
                  showTimeSelect={showTimes}
                  showTimeInput={showTimes}
                  dateFormat={SHORT_DATE_TIME_FORMAT}
                  minDate={availableRange?.from ? new Date(availableRange?.from) : null}
                  maxDate={localRange.to ? new Date(localRange.to) : null}
                  disabled={availableRange === null}
                  customInput={<TextField variant="outlined" size="small" style={{ marginBottom: 10 }} />}
                  popperPlacement="bottom-end"
                />
                <DatePicker
                  key="to"
                  selected={localRange.to ? new Date(localRange.to) : null}
                  onChange={(date: Date | null) => onToChange(date as Date)}
                  selectsEnd
                  showTimeSelect={showTimes}
                  showTimeInput={showTimes}
                  minDate={localRange.from ? new Date(localRange.from) : null}
                  maxDate={availableRange?.to ? new Date(availableRange?.to) : null}
                  dateFormat={SHORT_DATE_TIME_FORMAT}
                  disabled={availableRange === null}
                  customInput={<TextField variant="outlined" size="small"/>}
                  popperPlacement="bottom-end"
                />
                {showApplyAvailableRange && (
                  <Button color="primary" onClick={applyAvailableRange}>
                    {t('widgets.timerangePicker.availableRange')}
                  </Button>
                )}
              </div>
              <SelectMenu
                options={rangeFromNowOptions}
                onClick={onChangeRelative}
                selectedOption={selectedOption}
                availableRange={availableRange}
              />
            </div>
            <Stack spacing={1}>
              {exceedsCap && !!rangeCapMsg && <Alert severity="warning">{rangeCapMsg}</Alert>}
              <Stack direction="row" justifyContent="space-between">
                <Button
                  onClick={onClickApplyChanges}
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={exceedsCap}
                >
                  {t('widgets.timerangePicker.applyChange')}
                </Button>
                <Button onClick={handleReset} variant="text" color="primary" size="small">
                  {t('widgets.timerangePicker.reset')}
                </Button>
              </Stack>
            </Stack>
          </Panel>
        </Popper>
      </ClickAwayListener>
    </>
  );
}
